import { getPievePreferredResponseLanguageOption, toSafeObject as toObject, toSafeString } from '@shared/utils';
import getPieveAuthToken from '@/lib/auth/getPieveAuthToken.js';
import { normalizeVoiceTranscriptionInputProvenance } from '@/lib/pieveInputProvenance.js';
import hydrateThreadMeta from '@/redux/actions/pieveActions/hydrateThreadMeta.js';
import { startDirectRemoteThreadRun, steerDirectRemoteThreadRun } from '@/redux/actions/pieveActions/lib/directRemoteThreadRunTransport.js';
import createRequestFailure from '@/redux/requests/lib/createRequestFailure.js';
import { settingValue as selectSettingValue } from '@/redux/selectors/settingsSelectors.js';

const resolvePreferredResponseLanguageFromPrefs = (state) => {
  return toSafeString(selectSettingValue(state || {}, 'preferredResponseLanguage'));
};

const selectThreadMeta = (state, threadId) => {
  const normalizedThreadId = String(threadId || '').trim();
  const threads = Array.isArray(state?.pieve?.threads) ? state.pieve.threads : [];

  if (!normalizedThreadId) {
    return null;
  }

  return (
    threads.find((thread) => {
      return String(thread?.id || thread?.threadId || '').trim() === normalizedThreadId;
    }) || null
  );
};

/** The web app as the runtime that starts a run. */
const WEB_RUNTIME_CLIENT = Object.freeze({ kind: 'web', platform: 'web' });

/** Starts a run in a thread (or steers the running one) and follows its live events into the store. */
const sendPrompt = ({ mode = 'start', threadId, runId = null, expectedRunId = null, prompt, newUserInput = null, eventUpdate = null, model, reasoning, runtime, inputProvenance = null, projectId = null, projectName = null, projectWorkingDir = null, projectFolderName = null, devTrace = null, optimisticIdempotencyKey = null } = {}) => {
  return async (dispatch, getState) => {
    const normalizedMode = String(mode || 'start')
      .trim()
      .toLowerCase();

    const token = await getPieveAuthToken();

    if (normalizedMode === 'steer') {
      if (!threadId) {
        return createRequestFailure({ message: 'threadId is required' });
      }

      const normalizedRunId = String(runId || '').trim();
      const normalizedExpectedRunId = String(expectedRunId || normalizedRunId).trim();
      const normalizedInput = String(prompt || '').trim();

      if (!normalizedRunId) {
        return createRequestFailure({ message: 'runId is required' });
      }

      if (!normalizedExpectedRunId) {
        return createRequestFailure({ message: 'expectedRunId is required' });
      }

      if (!normalizedInput) {
        return createRequestFailure({ message: 'prompt is required' });
      }

      return steerDirectRemoteThreadRun({
        dispatch,
        getState,
        threadId,
        runId: normalizedRunId,
        expectedRunId: normalizedExpectedRunId,
        input: normalizedInput,
        authToken: token
      });
    }

    const runtimeObject = {
      ...toObject(runtime)
    };
    const normalizedInputProvenance = normalizeVoiceTranscriptionInputProvenance({
      inputProvenance: inputProvenance || runtimeObject?.inputProvenance
    });
    const explicitPreferredResponseLanguage = toSafeString(runtimeObject?.preferredResponseLanguage);
    const prefPreferredResponseLanguage = resolvePreferredResponseLanguageFromPrefs(getState?.());
    const resolvedPreferredResponseLanguage = getPievePreferredResponseLanguageOption(explicitPreferredResponseLanguage || prefPreferredResponseLanguage || 'auto').value;

    delete runtimeObject.inputProvenance;
    delete runtimeObject.input_provenance;

    if (normalizedInputProvenance) {
      runtimeObject.inputProvenance = normalizedInputProvenance;
    }

    runtimeObject.preferredResponseLanguage = resolvedPreferredResponseLanguage;

    const runtimePayload = Object.keys(runtimeObject).length > 0 ? runtimeObject : undefined;
    const runAuthToken = token;

    let threadMeta = threadId ? selectThreadMeta(getState?.() || {}, threadId) : null;

    if (threadId && !threadMeta) {
      const threadMetaRes = await dispatch(
        hydrateThreadMeta({
          threadId
        })
      );

      if (!threadMetaRes?.success) {
        return threadMetaRes || createRequestFailure({ message: 'Failed to load task metadata' });
      }

      threadMeta = selectThreadMeta(getState?.() || {}, threadId);
    }

    const runtimeForRun = runtimePayload;

    const requestClient = { kind: WEB_RUNTIME_CLIENT.kind, platform: WEB_RUNTIME_CLIENT.platform, version: 1 };

    return startDirectRemoteThreadRun({
      dispatch,
      getState,
      runtimeClient: WEB_RUNTIME_CLIENT,
      thread: threadMeta,
      threadId,
      prompt,
      newUserInput,
      eventUpdate,
      model,
      reasoning,
      authToken: runAuthToken,
      runtime: runtimeForRun,
      projectId,
      projectName,
      projectWorkingDir,
      projectFolderName,
      inputProvenance: normalizedInputProvenance,
      devTrace,
      optimisticIdempotencyKey,
      client: requestClient
    });
  };
};

export default sendPrompt;
